import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import AuthLayout from '../../components/layout/AuthLayout';
import ProofUpload from '../../components/common/ProofUpload';
import Button from '../../components/common/Button';
import { ComponentConstants, ComponentContainerMap } from '../../constants/ComponentConstants';
import { useRplApi } from '../../hooks/useRplApi';
import { useCourseApi } from '../../hooks/useCourseApi';
import { useMemberApi } from '../../hooks/useMemberApi';

const ProofOfPaymentPage: React.FC = () => {
  const [uploadSuccess, setUploadSuccess] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const navigate = useNavigate();
  const location = useLocation();

  // Extract query params
  const searchParams = new URLSearchParams(location.search);
  const componentId = searchParams.get('componentId');
  const entityId = searchParams.get('entityId');
  const memberId = searchParams.get('memberId');

  // API hooks
  const { updateProofOfPayment: updateRplProofOfPayment } = useRplApi();
  const { updateProofOfPayment: updateCourseProofOfPayment } = useCourseApi();
  const { updateProofOfPayment: updateMemberProofOfPayment } = useMemberApi();

  // Validate component ID
  const isValidComponentId = componentId && Object.keys(ComponentContainerMap).includes(componentId);

  // Get component name for display
  const getComponentDisplayName = (id: string): string => {
    switch (id) {
      case ComponentConstants.RPL:
        return 'RPL';
      case ComponentConstants.DEDICATED_COURSES:
        return 'Course';
      case ComponentConstants.RENEWALS:
        return 'Renewal';
      case ComponentConstants.MEMBERS:
        return 'Membership';
      default:
        return 'Payment';
    }
  };

  // Handle entity update after file upload
  const handleUploadSuccess = async (fileRecordId: string): Promise<void> => {
    if (!entityId || !componentId) {
      throw new Error('Missing required data to update entity');
    }

    try {
      switch (componentId) {
        case ComponentConstants.RPL:
          await updateRplProofOfPayment(entityId, fileRecordId);
          break;
        case ComponentConstants.DEDICATED_COURSES:
          await updateCourseProofOfPayment(entityId, fileRecordId);
          break;
        case ComponentConstants.MEMBERS:
          await updateMemberProofOfPayment(entityId, fileRecordId);
          break;
        // Add more cases as needed
        default:
          throw new Error('Unsupported component type for update');
      }
      setUploadSuccess(true);
    } catch (err) {
      console.error('Error updating entity:', err);
      setError('Failed to update the record with proof of payment');
      throw err;
    }
  };

  // Missing required params
  if (!componentId || !entityId) {
    return (
      <AuthLayout
        title="Invalid Link"
        description="We're sorry, but we couldn't process your request because required information is missing."
      >
        <div className="mt-8">
          <p className="text-sm text-gray-400 text-center">
            Please check the link you received or contact support for assistance.
          </p>
          <div className="mt-6">
            <Button onClick={() => navigate('/')} className="w-full">
              Go to Home
            </Button>
          </div>
        </div>
      </AuthLayout>
    );
  }

  // Invalid component ID
  if (!isValidComponentId) {
    return (
      <AuthLayout
        title="Invalid Request"
        description="The component type specified is not valid for proof of payment uploads."
      >
        <div className="mt-8">
          <p className="text-sm text-gray-400 text-center">
            Please contact support if you believe this is an error.
          </p>
          <div className="mt-6">
            <Button onClick={() => navigate('/')} className="w-full">
              Go to Home
            </Button>
          </div>
        </div>
      </AuthLayout>
    );
  }

  // Error state
  if (error) {
    return (
      <AuthLayout
        title="Something Went Wrong"
        description={error}
      >
        <div className="mt-8">
          <p className="text-sm text-gray-400 text-center">
            Please try again later or contact support for assistance.
          </p>
          <div className="mt-6">
            <Button onClick={() => navigate('/')} className="w-full">
              Go to Home
            </Button>
          </div>
        </div>
      </AuthLayout>
    );
  }

  // Upload success state
  if (uploadSuccess) {
    return (
      <AuthLayout
        title="Upload Complete"
        description="Your proof of payment has been successfully uploaded."
      >
        <div className="mt-8">
          <div className="p-4 rounded-lg bg-green-50 dark:bg-green-900/20 mb-6">
            <p className="text-sm text-green-800 dark:text-green-200 text-center">
              Thank you! Your proof of payment is now being processed. 
              Verification may take up to 3 business days, after which your access will be activated.
            </p>
          </div>
          <div className="mt-6">
            <Button onClick={() => navigate('/')} className="w-full">
              Go to Home
            </Button>
          </div>
        </div>
      </AuthLayout>
    );
  }

  // Main upload form
  const displayName = getComponentDisplayName(componentId);

  return (
    <AuthLayout
      title={`${displayName} Proof of Payment`}
      description={`Please upload your proof of payment to complete your ${displayName.toLowerCase()} application.`}
    >
      <div className="mt-8">
        <ProofUpload
          componentId={componentId}
          entityId={entityId}
          memberId={memberId || undefined}
          onUploadSuccess={handleUploadSuccess}
        />
      </div>
    </AuthLayout>
  );
};

export default ProofOfPaymentPage;
